import { type ReactNode } from "react";
import { Provider as JotaiProvider } from "jotai";
import { IconContext } from "@phosphor-icons/react";
import { MotionConfig } from "framer-motion";

import { DialogProvider } from "@/shell/dialog";
import { PerfModeStyleSync } from "@/core/state/perfMode";

import { AuthProvider } from "./AuthProvider";
import { ConvexAuthProvider } from "./ConvexAuthProvider";
import { QueryProvider } from "./QueryProvider";
import { LibellesDeLApp } from "./LibellesDeLApp";
import { ThemeProvider } from "./ThemeProvider";
import { ToastsDeLApp } from "./ToastsDeLApp";

/**
 * Defaults appliques a TOUTES les icones rendues via `@/shared/ui/icons`.
 * weight="regular" = outline standard Phosphor (le plus proche du rendu
 * lucide stroke=2). Historique : fill -> illisible (X notamment), bold ->
 * trop epais. `color="currentColor"` laisse Tailwind/CSS piloter via
 * `text-foo`. Override possible icone par icone via la prop `weight`
 * (ex. <Heart weight="fill" /> pour un coeur plein qui ressort).
 */
const ICON_DEFAULTS = {
  weight: "regular" as const,
  color: "currentColor",
  mirrored: false,
};

/**
 * Provider tree, en ordre de dépendance (extérieur → intérieur) :
 *   LibellesDeLApp      → les mots de la librairie (« Fermer », « Annuler », la pagination…)
 *                         dans la langue de l'app, pour TOUT ce qui suit — dialogues et
 *                         toasts compris. Ne dépend que de i18next (monté avant le 1er render).
 *   AuthProvider        → WorkOS AuthKit (login state, getAccessToken)
 *   ConvexAuthProvider  → Convex client + bridge auth WorkOS (lit AuthProvider)
 *   JotaiProvider       → atom store
 *   QueryProvider       → TanStack Query (REST FastAPI / WS)
 *   ThemeProvider       → toggle .dark sur <html>
 *   DialogProvider      → useDialog() imperatif (replace window.alert/confirm/prompt)
 *   IconContext         → defaults Phosphor (weight="regular") pour toute l'app
 *   ToastsDeLApp        → la pile des `toaster()` de la librairie, après `children`
 *                         (une seule fois, sur TOUTES les routes — cf ToastsDeLApp.tsx)
 *
 * AuthProvider DOIT être au-dessus de ConvexAuthProvider pour que le bridge
 * puisse lire le contexte AuthKit. DialogProvider en dernier pour que tout
 * descendant puisse appeler `useDialog()` (login, onboarding, cockpit).
 *
 * `MotionConfig reducedMotion="user"` enveloppe l'app (sous JotaiProvider) :
 * framer-motion respecte alors le réglage OS « réduire les animations » et
 * dégrade les springs `layout` en instantané. Le pendant CSS est dans
 * `globals.css` (@media prefers-reduced-motion). `PerfModeStyleSync` (perfMode
 * parapluie, cf core/state/perfMode.ts) doit être sous JotaiProvider pour lire
 * l'atom et stamper `data-perf-mode` sur <html>.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <LibellesDeLApp>
      <AuthProvider>
        <ConvexAuthProvider>
          <JotaiProvider>
            <QueryProvider>
              <ThemeProvider>
                <DialogProvider>
                  <IconContext.Provider value={ICON_DEFAULTS}>
                    <MotionConfig reducedMotion="user">
                      <PerfModeStyleSync />
                      {children}
                      <ToastsDeLApp />
                    </MotionConfig>
                  </IconContext.Provider>
                </DialogProvider>
              </ThemeProvider>
            </QueryProvider>
          </JotaiProvider>
        </ConvexAuthProvider>
      </AuthProvider>
    </LibellesDeLApp>
  );
}
